class RuffooSlideshow extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.slides = Array.from(this.querySelectorAll('[data-ruffoo-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-ruffoo-dot]'));
    this.previousButton = this.querySelector('[data-ruffoo-previous]');
    this.nextButton = this.querySelector('[data-ruffoo-next]');
    this.querySelector('[data-ruffoo-pause]')?.remove();
    this.setArrowIcon(this.previousButton, 'previous');
    this.setArrowIcon(this.nextButton, 'next');
    this.index = 0;
    this.timer = null;
    this.autoplay = this.dataset.autoplay === 'true';
    this.pauseOnHover = this.dataset.pauseOnHover === 'true';
    this.interval = Math.max(3000, Number(this.dataset.interval) || 5000);
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.previousButton?.addEventListener('click', () => this.show(this.index - 1, true), { signal });
    this.nextButton?.addEventListener('click', () => this.show(this.index + 1, true), { signal });

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.show(index, true), { signal });
    });

    if (this.pauseOnHover) {
      this.addEventListener('mouseenter', () => this.stop(), { signal });
      this.addEventListener('mouseleave', () => this.start(), { signal });
      this.addEventListener('focusin', () => this.stop(), { signal });
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.start();
      }, { signal });
    }

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.stop();
      else this.start();
    }, { signal });

    document.addEventListener('shopify:block:select', (event) => {
      const slide = event.target.closest?.('[data-ruffoo-slide]');
      if (!slide || !this.contains(slide)) return;
      const index = this.slides.indexOf(slide);
      if (index >= 0) this.show(index, false);
    }, { signal });

    this.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      this.pointerStart = event.clientX;
    }, { signal });

    this.addEventListener('pointerup', (event) => {
      if (this.pointerStart == null) return;
      const distance = event.clientX - this.pointerStart;
      this.pointerStart = null;
      if (Math.abs(distance) < 50) return;
      distance > 0 ? this.show(this.index - 1, true) : this.show(this.index + 1, true);
    }, { signal });

    this.show(0, false);
    this.start();
  }

  disconnectedCallback() {
    this.stop();
    this.abortController?.abort();
    this.initialized = false;
  }

  setArrowIcon(button, direction) {
    if (!button) return;
    const namespace = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(namespace, 'svg');
    const path = document.createElementNS(namespace, 'path');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    svg.style.display = 'block';
    svg.style.width = '46%';
    svg.style.height = '46%';
    svg.style.margin = '0';
    svg.style.overflow = 'visible';
    path.setAttribute('d', direction === 'previous' ? 'M15 4.5 7.5 12 15 19.5' : 'M9 4.5 16.5 12 9 19.5');
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '2.5');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    svg.append(path);
    button.replaceChildren(svg);
  }

  show(nextIndex, userInitiated = false) {
    if (!this.slides.length) return;
    const count = this.slides.length;
    this.index = (nextIndex + count) % count;
    this.style.setProperty('--ruffoo-slide-index', String(this.index));

    this.slides.forEach((slide, index) => {
      const active = index === this.index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      slide.querySelectorAll('video').forEach((video) => {
        if (active) video.play().catch(() => {});
        else video.pause();
      });
    });

    this.dots.forEach((dot, index) => {
      const active = index === this.index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });

    if (userInitiated) {
      this.stop();
      this.start();
    }
  }

  start() {
    this.stop();
    if (!this.autoplay || this.slides.length < 2 || document.hidden) return;
    this.timer = window.setInterval(() => this.show(this.index + 1), this.interval);
  }

  stop() {
    if (this.timer) window.clearInterval(this.timer);
    this.timer = null;
  }
}

if (!customElements.get('ruffoo-slideshow')) {
  customElements.define('ruffoo-slideshow', RuffooSlideshow);
}
